/**
 * @providesModule EmailVerify
 *
 */

import React, {Component} from 'react';

import AWSManager from 'AWSManager';
import Background from 'Background';
import CurrentUserStore from 'CurrentUserStore';
import Divider from 'Divider';
import GlobalEventEmitter from 'GlobalEventEmitter';
import Label from 'Label';
import LoadingIndicator from 'LoadingIndicator';
import {_m} from 'INTL';
import ModalNavigationController from 'ModalNavigationController';
import NavigationBar from 'NavigationBar';
import NavigationManager from 'NavigationManager';
import NetClient from 'NetClient';
import OrientationTransitionController from 'OrientationTransitionController';
import PushButton from 'PushButton';
import Sizes from 'Sizes';
import SkillzImage from 'SkillzImage';
import SkillzStyle from 'SkillzStyle';
import StaticWarningModal from 'StaticWarningModal';
import styles from 'EmailVerifyStyle';

import {
  Alert,
  Keyboard,
  NativeModules,
  Platform,
  ScrollView,
  StatusBar,
  TextInput,
  View,
} from 'react-native';

const SkillzAPIManager = NativeModules.SKZAPIManager;
const SkillzModule = NativeModules.SkillzModule;

const {btnGradientOne, viewCornerRadiusPrimary} = SkillzStyle.fullStyles;

type IEmailVerifyProps = {
  onDone: () => void;
  navigator: any;
  shouldShowOTANavBar: boolean;
  onBackButtonPress: () => void;
  prohibitDisableKeyboardManager: boolean;
  isShowingAndroidStatusBar: boolean;
  onSendEmailTrackingEvent: string;
};

type IEmailVerifyState = {
  shouldNotRender: boolean;
  userEmail: string;
  buttonEnabled: boolean;
  isFocused: boolean;
};

const ANDROID_STATUS_BAR_PADDING =
  Platform.OS === 'android' ? {marginTop: StatusBar.currentHeight} : {};

export default class EmailVerify extends Component<IEmailVerifyProps, IEmailVerifyState> {
  verifyEmailTimeout: any;
  subscriptions: never[];
  constructor(props: IEmailVerifyProps) {
    super(props);
    this.state = {
      userEmail: '',
      buttonEnabled: false,
      shouldNotRender: false,
    };
  }

  componentDidMount() {
    GlobalEventEmitter.emit('toggleShouldRender', false);
    this._addListeners();
    if (Platform.OS !== 'web') {
      NavigationManager.disableSideMenu();
    }
    if (Platform.OS == 'android') {
      SkillzModule.setEnabledKeyboardManager(true);
    } else {
      if (this.props.prohibitDisableKeyboardManager) {
        SkillzModule.setEnabledKeyboardManager(true);
      }
    }
    CurrentUserStore.fetchUser(true).then((user: Object) => {
      this.setState({userEmail: user.email});
      if (user.email) {
        this.setState({buttonEnabled: true});
      }
    });

    this.startEmailVerifyPoll();
  }

  startEmailVerifyPoll() {
    if (!this.verifyEmailTimeout) {
      this.verifyEmailTimeout = setTimeout(() => {
        console.debug('EmailVerify: Verifying user');
        CurrentUserStore.fetchUser(false).then((user: Object) => {
          if (this.verifyEmailTimeout) {
            clearTimeout(this.verifyEmailTimeout);
            this.verifyEmailTimeout = null;
          }
          if (user.email_verified) {
            console.debug('EmailVerify: User is verified');
            if (this.props.onDone) {
              this.props.onDone();
            } else {
              if (Platform.OS !== 'web') {
                this.props.navigator.popToRoot();
              }
            }
          } else {
            this.startEmailVerifyPoll();
            console.debug('EmailVerify: User is not verified, poll again');
          }
        });
      }, 2000);
    }
  }

  componentWillUnmount() {
    GlobalEventEmitter.emit('toggleShouldRender', true);
    this._removeListeners();
    if (Platform.OS == 'android') {
      if (!this.props.prohibitDisableKeyboardManager) {
        SkillzModule.setEnabledKeyboardManager(false);
      }
    } else {
      if (this.props.prohibitDisableKeyboardManager) {
        SkillzModule.setEnabledKeyboardManager(false);
      }
    }
    if (this.verifyEmailTimeout) {
      clearTimeout(this.verifyEmailTimeout);
      this.verifyEmailTimeout = null;
    }

    NavigationManager.enableSideMenu();
  }

  _addListeners() {
    this._removeListeners();
    this.subscriptions = [];
    this.subscriptions.push(
      Keyboard.addListener('keyboardDidShow', this._keyboardDidShow.bind(this))
    );
    this.subscriptions.push(
      Keyboard.addListener('keyboardDidHide', this._keyboardDidHide.bind(this))
    );
  }

  _keyboardDidShow(e: unknown) {
    this.setState({
      isFocused: true,
    });
  }

  _keyboardDidHide(e: unknown) {
    this.setState({
      isFocused: false,
    });
  }

  _removeListeners() {
    if (this.subscriptions) {
      this.subscriptions.forEach((listener: unknown): void => {
        if (listener) {
          listener.remove();
        }
      });
      this.subscriptions = [];
    }
  }

  _trackSendPressed() {
    if (this.props.onSendEmailTrackingEvent) {
      AWSManager.trackEvent(this.props.onSendEmailTrackingEvent);
    }
  }

  _sendEmailVerify(user: Object) {
    const url = 'users/' + user.id + '/email/verification_tokens';
    NetClient.postURL(
      url,
      null,
      SkillzAPIManager.OnError.failSilently,
      (error: Object, _payload: Object) => {
        LoadingIndicator.hide();
        if (!error) {
          const modalProps = {
            animationType: 'fade',
            transparent: true,
          };
          const modal = (
            <StaticWarningModal
              title={_m('Success')}
              message={_m('Verification email successfully sent')}
              buttonTitleArray={[_m('OK')]}
              callback={() => ModalNavigationController.hideModal()}
              titleTextStyling={styles.titleText}
              messageTextStyling={styles.messageText}
              buttonTextStyling={styles.buttonText}
            />
          );
          ModalNavigationController.showModal(modal, modalProps);
        } else {
          Alert.alert(_m('Error'), error.responseObject[0].message, [{text: _m('OK')}]);
        }
      }
    );
  }

  _onSendEmailPressed() {
    this._trackSendPressed();
    LoadingIndicator.show();
    const email = this.state.userEmail;
    if (email && email != '') {
      CurrentUserStore.fetchUser(false).then((user: Object) => {
        if (user.email && user.email == email) {
          this._sendEmailVerify(user);
        } else {
          // the user hasn't set up his email yet
          const patch = {email: email};
          NetClient.patchURL(
            'users/' + user.id,
            patch,
            SkillzAPIManager.OnError.failSilently,
            (error: Object, payload: Object) => {
              if (!error) {
                this._sendEmailVerify(payload);
              } else {
                LoadingIndicator.hide();
                Alert.alert(_m('Error'), error.responseObject[0].message, [{text: _m('OK')}]);
              }
            }
          );
        }
      });
    }
  }

  _renderEnvelope(): React.ReactNode {
    return (
      <SkillzImage
        style={styles.mailImageStyle}
        source={'ic_envelope_checked.png'}
        resizeMode={'contain'}
      />
    );
  }

  _renderDivider(): React.ReactNode {
    return (
      <Divider
        style={styles.divider}
        dividerColor={SkillzStyle.fullStyles.viewDividerColorTwo.backgroundColor}
      />
    );
  }

  _renderMessageLabel(): React.ReactNode {
    return (
      <View style={styles.messageContainer}>
        <Label style={styles.emailVerifyLabel}>{_m('Email Verification Required')}</Label>
        <Label numberOfLines={6} style={styles.requireMessageLabel}>
          {_m(
            'You must verify your email address to perform this action. Please check your email and follow the instructions to complete the verification process'
          ) + '.'}
        </Label>
      </View>
    );
  }

  _renderMessageAndEmailBox(): React.ReactNode {
    return (
      <View style={styles.messageAndEmailContainer}>
        {this._renderEnvelope()}
        {this._renderMessageLabel()}
        {this._renderDivider()}
        <View style={styles.emailInputContainer}>
          <TextInput
            underlineColorAndroid='transparent'
            onChangeText={(text: string) => {
              this.setState({userEmail: text});
              if (text != '') {
                this.setState({buttonEnabled: true});
              }
            }}
            onFocus={() => {
              this.setState({isFocused: true});
            }}
            onBlur={() => {
              this.setState({isFocused: false});
            }}
            onEndEditing={() => {
              this.setState({isFocused: false});
            }}
            keyboardType={'email-address'}
            multiline={false}
            value={this.state.userEmail}
            placeholder={'example@email.com'}
            placeholderTextColor={'grey'}
            returnKeyType={'done'}
            style={styles.emailInputText}
          />
        </View>
        {this._renderFooterButton()}
      </View>
    );
  }

  _renderFooterButton(): React.ReactNode {
    if (
      Platform.OS == 'android' &&
      this.state.isFocused &&
      !(Sizes.isPhone() && Sizes.isLandscape())
    ) {
      return false;
    }

    return (
      <View style={styles.footerButtonContainer}>
        <PushButton
          cornerRadius={viewCornerRadiusPrimary.borderRadius}
          style={styles.pushBtnContainer}
          colors={btnGradientOne}
          title={_m('SEND EMAIL')}
          enabled={this.state.buttonEnabled}
          onPress={this._onSendEmailPressed.bind(this)}
          textStyle={styles.pushBtnText}
        />
      </View>
    );
  }

  _renderHeader(): React.ReactNode {
    if (this.props.shouldShowOTANavBar) {
      const paddingStyle = this.props.isShowingAndroidStatusBar ? ANDROID_STATUS_BAR_PADDING : {};
      return (
        <NavigationBar
          hasBackButton={true}
          onBackButtonPress={() => {
            if (this.props.onBackButtonPress) {
              this.props.onBackButtonPress();
            }
            if (Sizes.isLandscape()) {
              OrientationTransitionController.updateOrientationToPortrait();
              this.setState({
                shouldNotRender: true,
              });
            }
            this.props.navigator.pop();
          }}
          showingStatusBarTranslucentAndroid={this.props.isShowingAndroidStatusBar}
        />
      );
    }
    return false;
  }

  render(): React.ReactNode {
    if (this.state.shouldNotRender) {
      return <Background />;
    }
    return (
      <Background style={styles.mainContainer}>
        {this._renderHeader()}
        <View style={styles.backgroundContainer}>
          <ScrollView centerContent={false}>{this._renderMessageAndEmailBox()}</ScrollView>
        </View>
      </Background>
    );
  }
}
